/**
 * Incautos y Cargos — pantalla unificada del módulo de incautos.
 *
 * La vista principal es el mantenimiento de incautos/cargos vigentes
 * embebido (ares5080: listado por tipo y fechas con cancelación por fila).
 * Tarjetas de acción en diálogos a pantalla completa:
 *   - "Registrar incauto" (ares5010, requiere autorización de un admin)
 *   - "Registrar cargo" (ares5060, máximo un cargo activo por préstamo)
 *   - "Reporte de incautos y cargos" (ares5095: facturado vs cobrado,
 *     con export e impresión)
 *   - "Incautadores" (catálogo maestinc; mismo permiso que registrar incauto)
 *   - "Modificar valor" (ares8070, antes en Configuración: corrige el valor
 *     de incautos/cargos no procesados)
 *
 * Sustituye las entradas de menú de registrarincauto.js, registrarcargo.js,
 * mantenimientoincautos.js e informecargos.js; los cuatro se reutilizan.
 */
import React, { useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import { useMaterialUIController } from "context";
import { FP, PageHeader, ActionCard, BarraVolver } from "components/financepro";
import { tienePermisosModulo } from "lib/seguridad/permisosPorModulo";
import RegistrarIncauto from "modules/incautos/registrarincauto";
import RegistrarCargo from "modules/incautos/registrarcargo";
import MantenimientoIncautos from "modules/incautos/mantenimientoincautos";
import InformeCargos from "modules/incautos/informecargos";
import ModificarIncautoCargo from "modules/configuracion/modificarincautocargo";
import Incautadores from "modules/incautos/incautadores";
import useAccionInicial from "lib/useAccionInicial";

export default function Incautos() {
  const [controller] = useMaterialUIController();
  const { roleDetalles } = controller;

  // Permisos por programa ARES (el backend re-valida en permisos.py).
  const puedeIncauto = tienePermisosModulo(roleDetalles, "ares5010");
  const puedeCargo = tienePermisosModulo(roleDetalles, "ares5060");
  const puedeMantenimiento = tienePermisosModulo(roleDetalles, "ares5080");
  const puedeReporte = tienePermisosModulo(roleDetalles, "ares5095");
  const puedeModificar = tienePermisosModulo(roleDetalles, "ares8070");

  const [incautoOpen, setIncautoOpen] = useState(false);
  const [cargoOpen, setCargoOpen] = useState(false);
  const [reporteOpen, setReporteOpen] = useState(false);
  const [modificarOpen, setModificarOpen] = useState(false);
  const [incautadoresOpen, setIncautadoresOpen] = useState(false);

  // Enlace profundo desde el buscador global (?accion=...).
  useAccionInicial({
    incauto: { permitido: puedeIncauto, abrir: () => setIncautoOpen(true) },
    cargo: { permitido: puedeCargo, abrir: () => setCargoOpen(true) },
    modificar: { permitido: puedeModificar, abrir: () => setModificarOpen(true) },
    reporte: { permitido: puedeReporte, abrir: () => setReporteOpen(true) },
    incautadores: { permitido: puedeIncauto, abrir: () => setIncautadoresOpen(true) },
  });

  // Incrementarlo recarga el mantenimiento embebido (tras registrar algo).
  const [refreshKey, setRefreshKey] = useState(0);
  const refrescar = () => setRefreshKey((k) => k + 1);

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Incautos y Cargos"
        subtitle="Registra, consulta y cancela incautos y cargos en un solo lugar"
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Acciones principales */}
        {(puedeIncauto || puedeCargo || puedeReporte || puedeModificar) && (
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            {puedeIncauto && (
              <Grid item xs={12} sm={6} md={4} lg={2.4}>
                <ActionCard
                  icon={DirectionsCarFilledOutlinedIcon}
                  titulo="Registrar incauto"
                  descripcion="Incautación del vehículo de un préstamo en mora (con autorización)"
                  color={FP.color.danger}
                  onClick={() => setIncautoOpen(true)}
                />
              </Grid>
            )}
            {puedeIncauto && (
              <Grid item xs={12} sm={6} md={4} lg={2.4}>
                <ActionCard
                  icon={BadgeOutlinedIcon}
                  titulo="Incautadores"
                  descripcion="Lista de incautadores autorizados para registrar incautos"
                  color={FP.color.warning}
                  onClick={() => setIncautadoresOpen(true)}
                />
              </Grid>
            )}
            {puedeCargo && (
              <Grid item xs={12} sm={6} md={4} lg={2.4}>
                <ActionCard
                  icon={GavelOutlinedIcon}
                  titulo="Registrar cargo"
                  descripcion="Cargo de gestión de cobro sobre un préstamo activo"
                  color={FP.color.accentBlue}
                  onClick={() => setCargoOpen(true)}
                />
              </Grid>
            )}
            {puedeModificar && (
              <Grid item xs={12} sm={6} md={4} lg={2.4}>
                <ActionCard
                  icon={EditOutlinedIcon}
                  titulo="Modificar valor"
                  descripcion="Corrige el valor de incautos/cargos no procesados"
                  color={FP.color.navy}
                  onClick={() => setModificarOpen(true)}
                />
              </Grid>
            )}
            {puedeReporte && (
              <Grid item xs={12} sm={6} md={4} lg={2.4}>
                <ActionCard
                  icon={AssessmentOutlinedIcon}
                  titulo="Reporte de incautos y cargos"
                  descripcion="Facturado vs cobrado por fechas y sucursal, con export"
                  color={FP.color.info}
                  onClick={() => setReporteOpen(true)}
                />
              </Grid>
            )}
          </Grid>
        )}

        {/* Vista principal: mantenimiento de incautos/cargos vigentes */}
        {puedeMantenimiento && <MantenimientoIncautos embedded refreshToken={refreshKey} />}
      </Box>

      {/* Registrar incauto (con flujo de autorización) */}
      <Dialog fullScreen open={incautoOpen} onClose={() => setIncautoOpen(false)}>
        <RegistrarIncauto embedded onExit={() => { setIncautoOpen(false); refrescar(); }} onSaved={refrescar} />
      </Dialog>

      {/* Catálogo de incautadores */}
      <Dialog fullScreen open={incautadoresOpen} onClose={() => setIncautadoresOpen(false)}>
        <Incautadores embedded onExit={() => setIncautadoresOpen(false)} />
      </Dialog>

      {/* Registrar cargo */}
      <Dialog fullScreen open={cargoOpen} onClose={() => setCargoOpen(false)}>
        <RegistrarCargo embedded onExit={() => { setCargoOpen(false); refrescar(); }} onSaved={refrescar} />
      </Dialog>

      {/* Modificar incauto/cargo (pantalla existente, con barra Volver) */}
      <Dialog fullScreen open={modificarOpen} onClose={() => setModificarOpen(false)}>
        <BarraVolver onExit={() => { setModificarOpen(false); refrescar(); }} />
        <ModificarIncautoCargo />
      </Dialog>

      {/* Reporte de incautos y cargos */}
      <Dialog fullScreen open={reporteOpen} onClose={() => setReporteOpen(false)}>
        <InformeCargos embedded onExit={() => setReporteOpen(false)} />
      </Dialog>
    </Box>
  );
}
